import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import doDataCheck from '../../services/doDataCheck';

import { PENALTIES_SHAPE } from '../../../../config/prop-types';

import loader from '../../../../../static/images/spinner-2x.gif';

/**
 * Displays hospital penalties data
 */
export default class PenaltiesTable extends React.PureComponent {
  constructor(props) {
    super(props);
    this.onColumnClick = this.onColumnClick.bind(this);
  }
  /**
   * Handles sort clicks for table headers
   */
  onColumnClick(column) {
    const { sortedColumn, sortOrder } = this.props;
    // If the sorted column has changed, reset the sort order
    let newSortOrder;
    if (sortedColumn !== column) {
      newSortOrder = 'DESC';
    } else {
      newSortOrder = ('DESC' === sortOrder) ? 'ASC' : 'DESC';
    }
    this.props.handleTableSortClick(column, newSortOrder);
  }
  /**
   * Displays the proper forward facing value for a penalization
   * @param {Object} penalty
   * @returns the value to display
   */
  getPenaltyDisplayValue(penalty) { // eslint-disable-line class-methods-use-this
    if ('Y' === penalty.value) {
      return 'Yes';
    } else if (0 < penalty.value) {
      return penalty.value;
    }
    return undefined;
  }
  /**
   * Renders the sort control
   * @param {String} type - the column being sorted
   * @returns {object} - JSX for the control
   */
  sortControl(type) {
    const { sortedColumn, sortOrder } = this.props;
    // 'ASC' === sortOrder; 'DESC' === sortOrder
    return (
      <div>
        <div className={classNames('upArrow', {
          active: type === sortedColumn && 'ASC' === sortOrder,
        })}
        />
        <div className={classNames('downArrow', {
          active: type === sortedColumn && 'DESC' === sortOrder,
        })}
        />
      </div>
    );
  }
  /**
   * @returns {object} - JSX for the component.
   */
  render() {
    const {
      activePenalty,
      data,
      loading,
      years,
    } = this.props;

    if (loading) {
      return (
        <div className="loading">
          <img src={loader} alt="" />
          <span>Loading</span>
        </div>
      );
    } else if (data.map((result) => // Only show if there are datapoints for the penalty type.
      doDataCheck(activePenalty, result.dataPoints))
      .some((result) => 0 < result)) {
      const caption =
        `Hospital penalizations ${years[0]} to ${years[years.length - 1]}`;
      return (
        <div className="penaltiesTable">
          <div className="key">
            <div className="item">
              <div className="color penalty" />
              <div className="title">Penalty</div>
            </div>
            <div className="item">
              <div className="color nopenalty" />
              <div className="title">No penalty</div>
            </div>
            <div className="item">
              <div className="color exempt" />
              <div className="title">Exempt</div>
            </div>
          </div>
          <table>
            <caption>{caption}</caption>
            <thead>
              <tr>
                <th scope="col">
                  <button
                    className="sort-by"
                    onClick={() =>
                      this.onColumnClick('NAME')}
                  >
                    Name
                    {this.sortControl('NAME')}
                  </button>
                </th>
                {years.map((year) => (
                  <th key={year} scope="col">
                    <button
                      className="sort-by"
                      onClick={() => this.onColumnClick(year)}
                    >
                      <span className="fullyear">{year}</span>
                      <span className="partialyear">
                        &apos;{year - 2000}
                      </span>
                      {this.sortControl(year)}
                    </button>
                  </th>
                  ))
                }
              </tr>
            </thead>
            <tbody>
              {data.map((hospital) => (
                doDataCheck(activePenalty, hospital.dataPoints) &&
                (
                  <tr key={hospital.id}>
                    <th className="hospital-info" scope="row">
                      <div className="hospital-name">
                        {hospital.name}
                      </div>
                      <div className="hospital-loc">
                        {hospital.city},&nbsp;
                        <button onClick={() =>
                          this.props.onFilter('activeState', hospital.state)}
                        >
                          {hospital.state}
                        </button>
                      </div>
                    </th>
                    {hospital.dataPoints
                      .filter((penalty) =>
                        activePenalty === penalty.type)
                      .map((penalty) => (
                        <td key={`${hospital.id}${penalty.year}`}>
                          <div className={classNames({
                            dataCell: true,
                            penalized: 0 < penalty.value
                              || 'Y' === penalty.value,
                            exempt: '*' === penalty.value,
                            na: 'NA' === penalty.value,
                          })}
                          >
                            {this.getPenaltyDisplayValue(penalty)}
                          </div>
                        </td>
                      ))}
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return (
      <div className="noresults">
        There are no records that match the currently selected filters.
      </div>
    );
  }
}

PenaltiesTable.propTypes = {
  activePenalty: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape(PENALTIES_SHAPE)).isRequired,
  handleTableSortClick: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  sortedColumn: PropTypes
  .oneOfType([PropTypes.number, PropTypes.string]),
  sortOrder: PropTypes.string.isRequired,
  onFilter: PropTypes.func.isRequired,
  years: PropTypes.instanceOf(Array).isRequired,
};

PenaltiesTable.defaultProps = {
  sortedColumn: null,
};
